"use client"

import React, { useState, useEffect } from "react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Loader2, Shield, Users, AlertCircle } from "lucide-react"
import { useToast } from "@/hooks/use-toast"
import axios from "axios"

export function UserPermissionsModal({ open, onOpenChange, user, onSuccess }) {
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [userPermissions, setUserPermissions] = useState([])
  const [departmentPermissions, setDepartmentPermissions] = useState([])
  const [availablePermissions, setAvailablePermissions] = useState({})
  const [userRoles, setUserRoles] = useState([])
  const [isAdmin, setIsAdmin] = useState(false)
  const [selectedPermissions, setSelectedPermissions] = useState([])
  const { toast } = useToast()

  useEffect(() => {
    if (open && user?.id) {
      fetchUserPermissions()
    }
  }, [open, user?.id])

  const fetchUserPermissions = async () => {
    try {
      setIsLoading(true)
      const response = await axios.get(`/users/${user.id}/permissions`)
      const data = response.data
      
      setUserPermissions(data.userPermissions || [])
      setDepartmentPermissions(data.departmentPermissions || [])
      setAvailablePermissions(data.availablePermissions || {})
      setUserRoles(data.userRoles || [])
      setIsAdmin(data.isAdmin || false)
      setSelectedPermissions(data.userPermissions || [])
    } catch (error) {
      console.error('Error fetching user permissions:', error)
      toast({
        title: "Error",
        description: "Failed to load user permissions.",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      await axios.put(`/users/${user.id}/permissions`, {
        permissions: selectedPermissions
      })

      toast({
        title: "Success",
        description: "User permissions updated successfully.",
      })

      onSuccess?.()
      onOpenChange(false)
    } catch (error) {
      console.error('Error updating user permissions:', error)
      toast({
        title: "Error",
        description: "Failed to update user permissions.",
        variant: "destructive",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handlePermissionToggle = (permissionName, checked) => {
    setSelectedPermissions(prev => 
      checked 
        ? [...prev, permissionName]
        : prev.filter(p => p !== permissionName)
    )
  }

  const handleCategoryToggle = (categoryPermissions, checked) => {
    setSelectedPermissions(prev => {
      if (checked) {
        return [...new Set([...prev, ...categoryPermissions])]
      } else {
        return prev.filter(p => !categoryPermissions.includes(p))
      }
    })
  }

  const formatPermissionName = (name) => {
    return name.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ')
  }

  const formatCategoryName = (category) => {
    return category.charAt(0).toUpperCase() + category.slice(1)
  }

  const formatRoleName = (role) => {
    return role.charAt(0).toUpperCase() + role.slice(1)
  }

  if (!user) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Manage Permissions - {user.firstName} {user.lastName}
          </DialogTitle>
          <DialogDescription>
            Configure individual permissions for this user. Admin users automatically have all permissions.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="space-y-6 py-4">
              {/* User Info */}
              <Card>
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-medium">User Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-sm font-medium">{user.firstName} {user.lastName}</p>
                      <p className="text-xs text-muted-foreground">{user.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {userRoles.map((role) => (
                      <Badge key={role} variant="outline">
                        {formatRoleName(role)}
                      </Badge>
                    ))}
                  </div>

                  {user.department && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Users className="h-4 w-4" />
                      Department: {user.department}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Department Permissions Info */}
              {departmentPermissions.length > 0 && (
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm font-medium flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      Department Default Permissions
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-xs text-muted-foreground mb-3">
                      These permissions are inherited from the user's department role.
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {departmentPermissions.map((permission) => (
                        <Badge key={permission} variant="secondary" className="text-xs">
                          {formatPermissionName(permission)}
                        </Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Admin Warning */}
              {isAdmin && (
                <Card className="border-yellow-200 bg-yellow-50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2 text-yellow-800">
                      <Shield className="h-4 w-4" />
                      <p className="text-sm font-medium">
                        This user has admin role and automatically receives all permissions.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Permissions */}
              <div className="space-y-4">
                <Label className="text-base font-medium">Individual Permissions</Label>
                {Object.entries(availablePermissions).map(([category, permissions]) => {
                  const categoryPermissions = permissions.map(p => p.name)
                  const allSelected = categoryPermissions.every(p => selectedPermissions.includes(p))
                  const someSelected = categoryPermissions.some(p => selectedPermissions.includes(p))
                  
                  return (
                    <Card key={category} className="p-4">
                      <div className="space-y-3">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id={`category-${category}`}
                            checked={allSelected}
                            ref={ref => {
                              if (ref) ref.indeterminate = someSelected && !allSelected
                            }}
                            onCheckedChange={(checked) => 
                              handleCategoryToggle(categoryPermissions, checked)
                            }
                            disabled={isAdmin}
                          />
                          <Label 
                            htmlFor={`category-${category}`}
                            className="text-sm font-medium"
                          >
                            {formatCategoryName(category)}
                          </Label>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 ml-6">
                          {permissions.map((permission) => {
                            const isDepartmentDefault = departmentPermissions.includes(permission.name)
                            return (
                              <div key={permission.name} className="flex items-center space-x-2">
                                <Checkbox
                                  id={permission.name}
                                  checked={selectedPermissions.includes(permission.name)}
                                  onCheckedChange={(checked) => 
                                    handlePermissionToggle(permission.name, checked)
                                  }
                                  disabled={isAdmin}
                                />
                                <Label 
                                  htmlFor={permission.name}
                                  className={`text-sm ${isDepartmentDefault ? 'text-blue-600' : ''}`}
                                >
                                  {formatPermissionName(permission.name)}
                                  {isDepartmentDefault && (
                                    <Badge variant="outline" className="ml-1 text-xs">
                                      Dept
                                    </Badge>
                                  )}
                                </Label>
                              </div>
                            )
                          })}
                        </div>
                      </div>
                    </Card>
                  )
                })}
              </div>
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => onOpenChange(false)}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={isSubmitting || isAdmin}
              >
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Update Permissions
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
} 